import {
  Chat,
  ChatMessage,
  ChatCreationRequest,
  ChatUpdateRequest,
  MessageCreationRequest,
  TripAttachmentRequest,
  ChatListResponse,
  ChatDetailResponse,
  MessageListResponse,
  TripAttachmentResponse,
} from '../types/chat';

/**
 * Chat API functions for managing chat conversations and messages
 */

import axiosInstance from '../utils/axiosInstance';
import { createLogger } from '../utils/logger';

// Use relative paths since axiosInstance has baseURL configured
const CHATS_BASE = '/api/chats';
const log = createLogger('chatApi');

// ============================================================================
// CHAT CRUD OPERATIONS
// ============================================================================

/**
 * Create a new chat conversation
 */
export const createChat = async (request: ChatCreationRequest): Promise<{ success: boolean; chat: Chat }> => {
  try {
    const response = await axiosInstance.post(CHATS_BASE, request);
    return response.data;
  } catch (error: any) {
    log.error('Error creating chat', {
      error: error.message,
      response: error.response?.data,
      status: error.response?.status,
      isNetworkError: !error.response && error.request,
    });
    
    // Provide more helpful error messages
    if (!error.response && error.request) {
      throw new Error(
        `Network error: Unable to connect to chat API. ` +
        `Please check your internet connection and that the backend is running. ` +
        `If this persists, the backend may not be configured correctly for CORS.`
      );
    }
    
    throw new Error(`Failed to create chat: ${error.response?.data?.detail || error.message}`);
  }
};

/**
 * Get user's chat list (paginated)
 */
export const getUserChats = async (
  limit: number = 50,
  skip: number = 0,
  includeArchived: boolean = false
): Promise<ChatListResponse> => {
  try {
    const response = await axiosInstance.get(CHATS_BASE, {
      params: { limit, skip, include_archived: includeArchived },
    });
    return response.data;
  } catch (error: any) {
    log.error('Error getting user chats', error);
    throw new Error(`Failed to get user chats: ${error.response?.data?.detail || error.message}`);
  }
};

/**
 * Get specific chat by ID with full details
 */
export const getChatById = async (chatId: string): Promise<ChatDetailResponse> => {
  try {
    const response = await axiosInstance.get(`${CHATS_BASE}/${chatId}`);
    return response.data;
  } catch (error: any) {
    log.error(`Error getting chat ${chatId}`, error);
    throw new Error(`Failed to get chat: ${error.response?.data?.detail || error.message}`);
  }
};

/**
 * Update chat metadata
 */
export const updateChat = async (
  chatId: string,
  updates: ChatUpdateRequest
): Promise<{ success: boolean; chat: Chat }> => {
  try {
    const response = await axiosInstance.patch(`${CHATS_BASE}/${chatId}`, updates);
    return response.data;
  } catch (error: any) {
    log.error(`Error updating chat ${chatId}`, error);
    throw new Error(`Failed to update chat: ${error.response?.data?.detail || error.message}`);
  }
};

/**
 * Delete a chat (soft delete by default)
 */
export const deleteChat = async (
  chatId: string,
  permanent: boolean = false
): Promise<{ success: boolean; message: string }> => {
  try {
    const response = await axiosInstance.delete(`${CHATS_BASE}/${chatId}`, {
      params: { permanent },
    });
    return response.data;
  } catch (error: any) {
    log.error(`Error deleting chat ${chatId}`, error);
    throw new Error(`Failed to delete chat: ${error.response?.data?.detail || error.message}`);
  }
};

// ============================================================================
// MESSAGE OPERATIONS
// ============================================================================

/**
 * Add a new message to a chat
 */
export const createMessage = async (
  chatId: string,
  request: MessageCreationRequest
): Promise<{ success: boolean; message: ChatMessage }> => {
  try {
    const response = await axiosInstance.post(`${CHATS_BASE}/${chatId}/messages`, request);
    return response.data;
  } catch (error: any) {
    log.error(`Error creating message in chat ${chatId}`, error);
    throw new Error(`Failed to create message: ${error.response?.data?.detail || error.message}`);
  }
};

/**
 * Get messages for a chat (paginated)
 */
export const getChatMessages = async (
  chatId: string,
  limit: number = 100,
  skip: number = 0,
  includeDeleted: boolean = false
): Promise<MessageListResponse> => {
  try {
    const response = await axiosInstance.get(`${CHATS_BASE}/${chatId}/messages`, {
      params: { limit, skip, include_deleted: includeDeleted },
    });
    return response.data;
  } catch (error: any) {
    log.error(`Error getting messages for chat ${chatId}`, error);
    throw new Error(`Failed to get messages: ${error.response?.data?.detail || error.message}`);
  }
};

/**
 * Delete a message (soft delete)
 */
export const deleteMessage = async (
  chatId: string,
  messageId: string
): Promise<{ success: boolean; message: string }> => {
  try {
    const response = await axiosInstance.delete(`${CHATS_BASE}/${chatId}/messages/${messageId}`);
    return response.data;
  } catch (error: any) {
    log.error(`Error deleting message ${messageId}`, error);
    throw new Error(`Failed to delete message: ${error.response?.data?.detail || error.message}`);
  }
};

// ============================================================================
// TRIP ATTACHMENT OPERATIONS
// ============================================================================

/**
 * Attach a trip to a chat for context
 */
export const attachTripToChat = async (
  chatId: string,
  request: TripAttachmentRequest
): Promise<TripAttachmentResponse> => {
  try {
    const response = await axiosInstance.post(`${CHATS_BASE}/${chatId}/trips/attach`, request);
    return response.data;
  } catch (error: any) {
    log.error(`Error attaching trip to chat ${chatId}`, error);
    throw new Error(`Failed to attach trip: ${error.response?.data?.detail || error.message}`);
  }
};

/**
 * Detach a trip from a chat
 */
export const detachTripFromChat = async (
  chatId: string,
  searchId: string
): Promise<TripAttachmentResponse> => {
  try {
    const response = await axiosInstance.delete(`${CHATS_BASE}/${chatId}/trips/${searchId}`);
    return response.data;
  } catch (error: any) {
    log.error(`Error detaching trip ${searchId} from chat ${chatId}`, error);
    throw new Error(`Failed to detach trip: ${error.response?.data?.detail || error.message}`);
  }
};

/**
 * Set a trip as the active/current trip being discussed
 */
export const setActiveTrip = async (
  chatId: string,
  searchId: string
): Promise<{ success: boolean; chat_id: string; active_trip_id: string }> => {
  try {
    const response = await axiosInstance.patch(`${CHATS_BASE}/${chatId}/trips/${searchId}/set-active`);
    return response.data;
  } catch (error: any) {
    log.error(`Error setting active trip ${searchId} in chat ${chatId}`, error);
    throw new Error(`Failed to set active trip: ${error.response?.data?.detail || error.message}`);
  }
};

// ============================================================================
// UTILITY/HELPER FUNCTIONS
// ============================================================================

/**
 * Format chat timestamp for display
 */
export const formatChatTimestamp = (timestamp: string): string => {
  const date = new Date(timestamp);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;

  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

/**
 * Generate a session ID for new chats
 */
export const generateSessionId = (): string => {
  return `session_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
};
